import * as signalR from "@aspnet/signalr";
import { LocationDescriptor } from "history";
import { List } from "immutable";
import React from "react";
import { push, replace } from "react-router-redux";
import * as redux from "redux";
import * as Actions from "../../actions";
import * as M from "../../models";
import { Api } from "../../services/Api";
import { AppState } from "../../store";
import { AuthHelper } from "../../utils";
import { ErrorAlert } from "../generic";

export const DefaultStatePropsMap = (state: AppState, ownProps: OwnProps | undefined): DefaultConnectedState => ({
    abstractsWebsocket: state.abstract.websocket,
    accessToken: state.auth.accessToken || "",
    api: new Api(state),
    authToken: state.auth.accessToken || "",
    countries: state.countries.countries,
    isReconnected: state.conn.connected,
    lastPath: state.nav.pathHistory.size > 1
        ? state.nav.pathHistory.get(state.nav.pathHistory.size - 2)
        : undefined,
    tokenData: AuthHelper.tokenData(state.auth),
    websocket: state.dbSubscriptions.websocket,
});

export const DefaultDispatchPropsMap = (dispatch: redux.Dispatch<AppState>): DefaultConnectedDispatch => ({
    alertError: (msg: M.Alert.AlertMessage, title?: M.Alert.AlertTitle) => dispatch(Actions.Alert.error(msg, title)),
    alertInfo: (msg: M.Alert.AlertMessage, title?: M.Alert.AlertTitle) => dispatch(Actions.Alert.info(msg, title)),
    alertSuccess: (msg: M.Alert.AlertMessage, title?: M.Alert.AlertTitle) => dispatch(Actions.Alert.success(msg, title)),
    alertWarn: (msg: M.Alert.AlertMessage, title?: M.Alert.AlertTitle) => dispatch(Actions.Alert.warn(msg, title)),
    forceAuthRefresh: () => dispatch(Actions.Auth.forceAuthRefresh()),
    goto: (url: string, state?: any) => {
        if (url.indexOf("http") === 0)
            window.location.href = decodeURIComponent(url);
        else
            dispatch(push(url, state));
    },
    replace: (location: LocationDescriptor, state?: any) => dispatch(replace(location, state)),
    notify: (message: string, type: M.Notification.NotificationType, target?: M.Notification.NotificationTarget) =>
        dispatch(Actions.Noti.addNotification({ message, target, type })),
    notifyError: (message: string, icon?: string) => dispatch(Actions.Noti.notifyError(message, icon)),
    notifyInfo: (message: string, icon?: string) => dispatch(Actions.Noti.notifyInfo(message, icon)),
    notifySuccess: (message: string, icon?: string) => dispatch(Actions.Noti.notifySuccess(message, icon)),
    notifyWarning: (message: string, icon?: string) => dispatch(Actions.Noti.notifyWarning(message, icon)),
    replaceUrlQuery: (query: object, overwrite?: boolean, state?: any) => dispatch(Actions.Router.replaceQueryParams(query, overwrite, state)),
    updateChildOfPath: (path?: string) => dispatch(Actions.Nav.updateChildOfPath(path)),
    updateUrlQuery: (query: object, overwrite?: boolean, state?: any) => dispatch(Actions.Router.updateQueryParams(query, overwrite, state)),
});

export interface DefaultConnectedState {
    abstractsWebsocket?: signalR.HubConnection;
    accessToken: string;
    api: Api;
    authToken: string;
    countries: List<M.Country.Country>;
    isReconnected: boolean;
    lastPath?: string;
    tokenData: M.Auth.AuthTokenData;
    websocket?: signalR.HubConnection;
}

export interface DefaultConnectedDispatch {
    alertError: (msg: M.Alert.AlertMessage, title?: M.Alert.AlertTitle) => void;
    alertInfo: (msg: M.Alert.AlertMessage, title?: M.Alert.AlertTitle) => void;
    alertSuccess: (msg: M.Alert.AlertMessage, title?: M.Alert.AlertTitle) => void;
    alertWarn: (msg: M.Alert.AlertMessage, title?: M.Alert.AlertTitle) => void;
    forceAuthRefresh: () => void;
    goto: (url: string, state?: any) => void;
    replace: (location: LocationDescriptor, state?: any) => void;
    notify: (message: string, type: M.Notification.NotificationType, target?: M.Notification.NotificationTarget) => void;
    notifyError: (message: string, icon?: string) => void;
    notifyInfo: (message: string, icon?: string) => void;
    notifySuccess: (message: string, icon?: string) => void;
    notifyWarning: (message: string, icon?: string) => void;
    replaceUrlQuery: (query: object, overwrite?: boolean, state?: any) => void;
    updateChildOfPath: (path?: string) => void;
    updateUrlQuery: (query: object, overwrite?: boolean, state?: any) => void;
}

interface OwnProps {
}

interface OwnState {
    errors?: string[];
    errorsTitle?: string;
    loading?: boolean;
}

export class BaseComponent<TProps, TState> extends React.Component<TProps & OwnProps, TState & OwnState> {

    /** Tracks mounted state of component */
    public _isMounted = false;

    private loadingTimer?: NodeJS.Timer;

    public constructor(props: any) {
        super(props);
    }

    public clearErrors() {
        this.setErrors([]);
    }

    public async clearStateAsync() {
        await this.setStateAsync({
            ...this.state,
            errors: undefined,
            errorsTitle: undefined,
            loading: undefined,
        });
    }

    public getErrorAlert() {
        return <ErrorAlert errors={this.state.errors} title={this.state.errorsTitle} />;
    }

    public hasErrors() {
        return (this.getState().errors || []).length > 0;
    }

    public lastPathIsFrom(lastPath: string | undefined, pathInQuestion: string) {
        if (!lastPath)
            return false;
        return lastPath === pathInQuestion || lastPath.indexOf(`${pathInQuestion}?`) === 0;
    }

    public setErrors(errors: string[], title?: string) {
        this.setState(Object.assign(this.state, { errors, errorsTitle: title }));
    }

    public setStateAsync(newState: TState & OwnState) {
        return new Promise((resolve, reject) => {
            try {
                this.setState({
                    ...(this.state as any),
                    ...(newState as any),
                }, () => {
                    resolve(this.state);
                });
            }
            catch (err) {
                reject(err);
            }
        });
    }

    /**
     * Toggles loading state
     * @param doShow Whether to set loading to true or false
     * @param [timeout] Time after which to set loading state. Defaults to 275.
     */
    public toggleLoading(doShow: boolean, timeout: number = 275) {
        if (!this._isMounted)
            return;

        if (doShow) {
            // loading = true
            if (this.loadingTimer !== undefined)
                clearTimeout(this.loadingTimer);

            this.loadingTimer = setTimeout(() => {
                if (this._isMounted)
                    this.setState(Object.assign(this.state, { loading: true }));
            }, timeout);
        }
        else {
            // loading = false
            if (this.loadingTimer !== undefined)
                clearTimeout(this.loadingTimer);
            if (this._isMounted)
                this.setState(Object.assign(this.state, { loading: false }));
        }
    }

    public async toggleLoadingAsync(loading: boolean) {
        await this.setStateAsync({
            ...this.state,
            loading,
        });
    }

    private getState() {
        return this.state as OwnState;
    }
}



// WEBPACK FOOTER //
// ./src/components/base/BaseComponent.tsx